import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toastr from "toastr";
import { Modal, Button, Row, Col, Dropdown, OverlayTrigger, Tooltip, Spinner, ListGroup, Alert } from "react-bootstrap";
import { getAllActiveEmployees } from "../../redux/employees/actions";
import { getUsers } from "../../redux/auth/actions";
import { getDisciplinaryCases } from "../../redux/Modulo_Sanciones/diciplinarycase/actions";
import { getDataBoss, resetDataBoss } from "../../redux/Modulo_Sanciones/Audiences/actions";
import { updateAudience, deleteAudience, emailSend } from "../../redux/Modulo_Sanciones/Audiences/actions";

//Filtros de búsqueda de empleados
const filtersEmployees = [
  { id: "codigo", desc: "Codigo" },
  { id: "nombre", desc: "Nombre" },
];

// Validación hora entre 8:00 AM y 5:00 PM
const isValidHour = (value) => {
  if (!value) return false;
  const date = new Date(value);
  const hours = date.getHours();
  return hours >= 8 && hours <= 17;
};

// Validación fecha final sea al menos 30 minutos después de la fecha de inicio
const isValidEndDate = (startDate, endDate) => {
  if (!startDate || !endDate) return false;

  const start = new Date(startDate);
  const end = new Date(endDate);

  const differenceInMilliseconds = end - start;
  const minDifference = 30 * 60 * 1000;
  const maxDifference = 3 * 60 * 60 * 1000;

  return (
    differenceInMilliseconds >= minDifference &&
    differenceInMilliseconds <= maxDifference
  );
};

// Validaciones Yup
const validationSchema = Yup.object({
  title: Yup.string().required("**Campo requerido"),
  employee: Yup.string().required("**Campo requerido"),
  casodisciplinario: Yup.string(),
  startDate: Yup.date()
    .required("**Campo requerido")
    .typeError("Debe ser una fecha válida")
    .min(
      new Date().toISOString().split("T")[0],
      "La fecha de inicio debe ser posterior a la fecha actual"
    )
    .test(
      "valid-hour",
      "La hora debe estar entre las 8:00 AM y las 5:00 PM",
      isValidHour
    )
    .test(
      "not-in-past",
      "La fecha y hora deben ser posteriores a la actual",
      function (value) {
        if (!value) return true;
        const now = new Date();
        const inputDate = new Date(value);
        return inputDate > now;
      }
    ),
  endDate: Yup.date()
    .required("**Campo requerido")
    .typeError("Debe ser una fecha válida")
    .min(
      Yup.ref("startDate"),
      "La fecha final debe ser posterior a la fecha de inicio"
    )
    .test(
      "valid-hour",
      "La hora debe estar entre las 8:00 AM y las 5:00 PM",
      isValidHour
    )
    .test(
      "min-difference",
      "La fecha final debe ser entre 30 min y 3 hrs después de la fecha de inicio",
      function (value) {
        const { startDate } = this.parent;
        if (startDate && value) {
          return isValidEndDate(startDate, value);
        }
        return true;
      }
    ),
  supervisor: Yup.string(),
  comments: Yup.string().required("**Campo requerido"),
});

const AudiencesEditModal = ({ show, handleClose, selectedEvent }) => {

  const dispatch = useDispatch();

  //Estados iniciales
  const [searchTerm, setSearchTerm] = useState("");
  const [showEmployeeSearchModal, setShowEmployeeSearchModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState([]);
  const [employeesFilter, setEmployeesFilter] = useState("codigo");
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Store
  const { userId } = useSelector((store) => store.auth);
  const { employees, loaderAllEmployees, employeesFaild } = useSelector((store) => store.employees);
  const { loaderDiciplinaryCases, disciplinaryCases } = useSelector((store) => store.disciplinaryCase);
  const dataBoss = useSelector((state) => state.audiencesActions.dataBoss);

  // Función para formatear la fecha y hora
  const formatLocalDateTime = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  //Llena el formulario y obtiene casos disciplinarios asociados
  useEffect(() => {
    if (selectedEvent) {
      setSelectedEmployee({
        codigo: selectedEvent.extendedProps.empleado.id,
        nombre: selectedEvent.extendedProps.empleado.nombre,
      });
      dispatch(
        getDisciplinaryCases({
          tipo_busqueda: "empleado__codigo",
          filter: selectedEvent.extendedProps.empleado.id,
          vista: "administrador",
        })
      );
    }
  }, [selectedEvent, dispatch]);

  //Manejador de búsqueda de empleados

  const onSearchEmployee = (e) => {
    if (e.key === "Enter") {
      if (!searchTerm.trim()) {
        toastr.info("Por favor, ingresa un ID válido");
      } else {
        dispatch(
          getAllActiveEmployees({
            tipo_busqueda: employeesFilter,
            filter: searchTerm,
            vista: "administrador",
          })
        );
      }
    }
  };

  // Manejadores de eventos

  const handleEmployeeSelect = (employee) => {
    dispatch(getDataBoss(employee.codigo));
    dispatch(getUsers({ filter: employee.codigo }));
    dispatch(
      getDisciplinaryCases({
        tipo_busqueda: "empleado__codigo",
        filter: employee.codigo,
        vista: "administrador",
      })
    );
    setSelectedEmployee(employee);
    setShowEmployeeSearchModal(false);
  };

  const handleDeleteAudience = async () => {
    setIsDeleting(true);
    
    try {
      const id = selectedEvent?.extendedProps?.audience_id;

      //Empleado
      const dataEmailEmployee = {
        empleado_codigo: selectedEvent?.extendedProps?.empleado.id,
        empleado_correo:
          selectedEvent?.extendedProps?.empleado.correo_empresarial,
        accion: "eliminar",
        es_jefe: false,
      };

      // Jefe
      const dataEmailBoss = {
        empleado_codigo: selectedEvent?.extendedProps?.jefe_inmediato.id,
        empleado_correo:
          selectedEvent?.extendedProps?.jefe_inmediato.correo_empresarial,
        empleado_a_cargo_codigo: selectedEvent?.extendedProps?.empleado.id,
        accion: "eliminar",
        es_jefe: true,
      };

      dispatch(emailSend(dataEmailEmployee, id));
      dispatch(emailSend(dataEmailBoss, id));
      await dispatch(deleteAudience(id));
      handleCloseModal();

    } finally {
      setIsDeleting(false);
      // Oculta la modal de confirmación de eliminación
      setShowDeleteConfirmationModal(false);
    }
  };

  const handleUpdateAudience = async (data, id) => {
    setIsUpdating(true);
    try {

      await dispatch(updateAudience(data, id));

      // Jefe
      const dataEmailEmployee = {
        empleado_codigo: selectedEvent?.extendedProps?.empleado.id,
        empleado_correo:
          selectedEvent?.extendedProps?.empleado.correo_empresarial,
        accion: "actualizar",
        es_jefe: false, 
      };

      // Empleado
      const dataEmailBoss = {
        empleado_codigo: selectedEvent?.extendedProps?.jefe_inmediato.id,
        empleado_correo:
          selectedEvent?.extendedProps?.jefe_inmediato.correo_empresarial,
        empleado_a_cargo_codigo: selectedEvent?.extendedProps?.empleado.id,
        accion: "actualizar",
        es_jefe: true,
      };

      dispatch(emailSend(dataEmailBoss, id));
      dispatch(emailSend(dataEmailEmployee, id));

      handleCloseModal();

    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedEmployee("");
    dispatch(resetDataBoss());
    dispatch(resetDataBoss());
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleCloseModal} centered>
      <Modal.Header closeButton>
        <Modal.Title>Editar audiencia</Modal.Title>
      </Modal.Header>
      <Modal.Body className="mx-2">
        <Formik
          initialValues={{
            title: selectedEvent?.title || "",
            employee: selectedEvent?.extendedProps?.empleado.id
              ? `${selectedEvent?.extendedProps?.empleado.id}`
              : "",
            casodisciplinario:
              selectedEvent?.extendedProps?.caso_disciplinario.id || "",
            startDate: selectedEvent
              ? formatLocalDateTime(new Date(selectedEvent.start))
              : "",
            endDate: selectedEvent
              ? formatLocalDateTime(new Date(selectedEvent.end))
              : "",
            supervisor: selectedEvent?.extendedProps?.jefe_inmediato.id
              ? `${selectedEvent?.extendedProps?.jefe_inmediato.id}`
              : "",
            comments: selectedEvent?.extendedProps?.comentarios || "",
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={(values) => {
            // Convertir caso_disciplinario a número o null
            const casoDisciplinario =
              values.casodisciplinario === ""
                ? null
                : Number(values.casodisciplinario);

            const formData = {
              creado_por: userId,
              titulo: values.title,
              fecha_inicio: values.startDate,
              fecha_final: values.endDate,
              comentarios: values.comments,
              caso_disciplinario: casoDisciplinario,
            };

            try {
              dispatch(
                handleUpdateAudience(
                  formData,
                  selectedEvent?.extendedProps?.audience_id
                )
              );
              setSelectedEmployee("");
              handleCloseModal();
            } catch (error) {
              console.error("Error al actualizar la audiencia:", error);
            }
          }}
        >
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col md={12}>
                  <label htmlFor="employee">Empleado</label>
                  <div className="d-flex align-items-center">
                    <Field
                      as="input"
                      name="employee"
                      type="text"
                      className="form-control rounded-pill"
                      value={
                        selectedEmployee
                          ? `${selectedEmployee.codigo || ""}  ${
                              selectedEmployee.nombre || ""
                            }`
                          : ""
                      }
                      disabled
                    />
                    <OverlayTrigger
                      placement="auto"
                      overlay={
                        <Tooltip
                          style={{ zIndex: "99999999999999999999999999" }}
                        >
                          Seleccionar empleado
                        </Tooltip>
                      }
                    >
                      <Button
                        onClick={() => setShowEmployeeSearchModal(true)}
                        variant="light"
                        className="rounded-circle ml-2"
                        size="sm"
                        disabled={!!selectedEvent}
                      >
                        <i className="material-icons">group</i>
                      </Button>
                    </OverlayTrigger>
                  </div>
                  <ErrorMessage
                    name="employee"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <label htmlFor="casodisciplinario">
                    Casos disciplinarios
                  </label>
                  {loaderDiciplinaryCases ? (
                    <div className="d-flex justify-content-center align-items-center">
                      <span
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      />
                      <span className="ms-2">
                        Cargando casos Disciplinarios...
                      </span>
                    </div>
                  ) : (
                    <>
                      <Field
                        as="select"
                        name="casodisciplinario"
                        className="form-control rounded-pill"
                        disabled={
                          !selectedEmployee.codigo || loaderDiciplinaryCases
                        } 
                      >
                        <option value="">
                          Seleccione un caso disciplinario
                        </option>
                        {disciplinaryCases?.data?.length > 0 ? (
                          disciplinaryCases.data.map((cases) => {
                            let statusText = "";

                            switch (cases.estatus) {
                              case 2:
                                statusText = "Abierto";
                                break;
                              case 3:
                                statusText = "En Proceso";
                                break;
                              case 5:
                                statusText = "Cerrado";
                                break;
                              default:
                                statusText = "Desconocido";
                            }

                            return (
                              <option key={cases.id} value={cases.id}>
                                {cases.caso_disciplinario} - {statusText}
                              </option>
                            );
                          })
                        ) : (
                          <option value="" disabled>
                            No hay ningún caso disciplinario vinculado
                          </option>
                        )}
                      </Field>
                      <ErrorMessage
                        name="casodisciplinario"
                        component="div"
                        className="text-danger"
                      />
                    </>
                  )}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <label htmlFor="title">Título</label>
                  <Field
                    name="title"
                    type="text"
                    className="form-control rounded-pill"
                  />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <label htmlFor="startDate">Fecha de Inicio</label>
                  <Field
                    name="startDate"
                    type="datetime-local"
                    className="form-control rounded-pill"
                  />
                  <ErrorMessage
                    name="startDate"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <label htmlFor="endDate">Fecha Final</label>
                  <Field
                    name="endDate"
                    type="datetime-local"
                    className="form-control rounded-pill"
                  />
                  <ErrorMessage
                    name="endDate"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <label htmlFor="supervisor">Jefe Inmediato</label>
                  <Field
                    name="supervisor"
                    type="text"
                    className="form-control rounded-pill"
                    value={
                      dataBoss
                        ? `${dataBoss.jefe_inmediato.codigo} ${dataBoss.jefe_inmediato.nombre}`
                        : selectedEvent?.extendedProps?.jefe_inmediato.id
                        ? `${selectedEvent?.extendedProps?.jefe_inmediato.id} ${selectedEvent?.extendedProps?.jefe_inmediato.nombre}`
                        : ""
                    }
                    disabled
                  />
                  <ErrorMessage
                    name="supervisor"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <label htmlFor="comments">Comentarios</label>
                  <Field
                    name="comments"
                    as="textarea"
                    className="form-control"
                    rows={3}
                  />
                  <ErrorMessage
                    name="comments"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>
              <Modal.Footer>
                <Button
                  className="rounded-pill"
                  variant="secondary"
                  onClick={handleCloseModal}
                >
                  Cancelar
                </Button>

                <Button
                  className="rounded-pill"
                  variant="danger"
                  onClick={() => setShowDeleteConfirmationModal(true)}
                >
                  Eliminar
                </Button>

                <Button
                  className="rounded-pill"
                  variant="primary"
                  type="submit"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="ms-2">Actualizando...</span>
                    </>
                  ) : (
                    "Actualizar"
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Body>
      <Modal
        show={showDeleteConfirmationModal}
        onHide={() => setShowDeleteConfirmationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que deseas eliminar esta audiencia? Esta acción no se
          puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="rounded-pill"
            variant="secondary"
            onClick={() => setShowDeleteConfirmationModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAudience}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Eliminando...
              </>
            ) : (
              "Eliminar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de búsqueda de empleados */}
      <Modal
        show={showEmployeeSearchModal}
        onHide={() => setShowEmployeeSearchModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar Empleado</Modal.Title>
        </Modal.Header>
        <Modal.Body>

          <Row className="mb-3 align-items-center">

            <Col xs="auto">
              <span className="font-weight-bold">
                {employeesFilter === "codigo" ? "Código:" : "Nombre:"}
              </span>
            </Col>

            <Col>
              <input
                type="text"
                id="searchEmployee"
                className="form-control rounded-pill"
                placeholder={`Ingrese ${
                  employeesFilter === "codigo" ? "código" : "nombre"
                } del empleado`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={onSearchEmployee}
              />
            </Col>

            <Col xs="auto">
              <OverlayTrigger
                placement="auto"
                overlay={
                  <Tooltip style={{ zIndex: "99999999999999999999999999" }}>
                    Seleccionar Filtrado
                  </Tooltip>
                }
              >
                <Dropdown>
                  <Dropdown.Toggle
                    variant="light"
                    className="btn-sm font rounded-pill"
                  >
                    <i className="material-icons">filter_list</i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {filtersEmployees.map((item) => (
                      <Dropdown.Item
                        onClick={() => setEmployeesFilter(item.id)}
                        key={item.id}
                      >
                        {item.desc}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </OverlayTrigger>
            </Col>
          </Row>

          <div>
            {employeesFaild ? (
              <Alert variant="danger" className="my-4">
                {employeesFaild}
              </Alert>
            ) : null}
            {loaderAllEmployees ? (
              <div className="d-flex justify-content-center">
                <Spinner
                  animation="border"
                  role="status"
                  className="d-flex justify-content-center"
                />
              </div>
            ) : employees.length > 0 ? (
              <ListGroup variant="flush">
                {employees.map((employee) => (
                  <ListGroup.Item
                    key={employee.codigo}
                    className="d-flex flex-wrap justify-content-between align-items-center"
                  >
                    <p className="m-0">
                      {employee.codigo} {employee.nombre}
                    </p>
                    <Button
                      onClick={() => handleEmployeeSelect(employee)}
                      size="sm"
                      variant="light"
                      className="rounded-circle"
                    >
                      <i className="material-icons">person_add</i>
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              <Alert variant="info" className="my-4">
                Sin registros que mostrar.
              </Alert>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </Modal>
  );
};

export default AudiencesEditModal;
